import { useEffect, useRef, useCallback, useState } from 'react';
import { GoogleSSOButtonType, GoogleSSOButtonShape } from 'features/Connect/utils/connectEnums';
import { ATSSignUpDataTestIds } from 'data-testids/ATS';

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });

const GoogleLogin = ({
  googleSignin,
  buttonType = GoogleSSOButtonType.STANDARD,
  promptParentId,
  dataTestId,
}) => {
  const googleButton = useRef(null);
  const containerRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState();

  const handleCredentialResponse = useCallback(
    (response) => {
      if (googleSignin) {
        googleSignin(response.credential);
      }
    },
    [googleSignin]
  );

  useEffect(() => {
    if (buttonType !== GoogleSSOButtonType.STANDARD) return;
    function updateWidth() {
      if (containerRef.current) {
        setButtonWidth(containerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [buttonType]);

  useEffect(() => {
    if (buttonType === GoogleSSOButtonType.STANDARD && !buttonWidth) return;
    const src = 'https://accounts.google.com/gsi/client';
    const id = '951631884860-733u4aj58ariplgkg508lhds6hbf1cgi.apps.googleusercontent.com';

    const baseOptions = {
      theme: 'outline',
      logo_alignment: 'center',
      locale: 'en_US',
    };

    let buttonOptions;
    switch (buttonType) {
      case GoogleSSOButtonType.ICON:
        buttonOptions = {
          ...baseOptions,
          type: GoogleSSOButtonType.ICON,
          shape: GoogleSSOButtonShape.CIRCLE,
        };
        break;
      case GoogleSSOButtonType.PILL:
        buttonOptions = {
          ...baseOptions,
          type: GoogleSSOButtonType.PILL,
          shape: GoogleSSOButtonShape.PILL,
          text: 'signin',
        };
        break;
      case GoogleSSOButtonType.STANDARD:
      default:
        buttonOptions = {
          ...baseOptions,
          type: GoogleSSOButtonType.STANDARD,
          shape: GoogleSSOButtonShape.RECTANGULAR,
          width: buttonWidth,
        };
        break;
    }

    loadScript(src)
      .then(() => {
        /*global google*/
        google.accounts.id.initialize({
          client_id: id,
          callback: handleCredentialResponse,
          prompt_parent_id: promptParentId,
        });
        try {
          if (googleButton.current) {
            google.accounts.id.renderButton(googleButton.current, buttonOptions);
          }
        } catch (googleError) {
          // If rendering the standard button with width fails, try again without it.
          if (buttonType === GoogleSSOButtonType.STANDARD) {
            const { width, ...fallbackOptions } = buttonOptions;
            if (googleButton.current) {
              google.accounts.id.renderButton(googleButton.current, fallbackOptions);
            }
          }
          throw new Error(googleError);
        }
      })
      .catch(console.error);

    return () => {
      const scriptTag = document.querySelector(`script[src="${src}"]`);
      if (scriptTag) document.body.removeChild(scriptTag);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonType, promptParentId, buttonWidth]);

  if (buttonType === GoogleSSOButtonType.STANDARD) {
    return (
      <div ref={containerRef} style={{ width: '100%' }}>
        <div ref={googleButton} data-testid={dataTestId} />
      </div>
    );
  } else {
    return <div ref={googleButton} data-testid={dataTestId} />;
  }
};

export default GoogleLogin;
